<div class="container-fluid" xmlns="http://www.w3.org/1999/html">

  <app-page-title [title]="'Semantic Embedder API'" [icon]="'bi-diagram-3'">
    <a class="btn btn-light"
       href="https://github.com/explainers-by-googlers/semantic-embedder-api"
       target="_blank"><i class="bi bi-file-text"></i> <span class="ms-2">Explainer</span></a>
    <a class="btn btn-light disabled"
       href="https://github.com/explainers-by-googlers/semantic-embedder-api"
       target="_blank"><i class="bi bi-body-text"></i> <span class="ms-2">Specifications</span></a>
  </app-page-title>

  <div class="row mb-3">
    <div class="col-12">
      <app-page-accordion
        [requirementsStatus]="this.apiFlag.status"
        (checkRequirementsEvent)="checkRequirements()"
        [requirements]="getRequirements()"
      >
        <div debugInformation>
          <p>The Semantic Embedder API is an <strong>early design sketch</strong> from the Chrome Built-in AI team and
            has not been approved to ship in Chrome. The surface below follows the explainer and may change.</p>
          <p class="mt-1">Embeddings are only comparable within the same embedding space: check
            <span class="code">result.metadata.embeddingSpace</span> before comparing vectors you stored earlier.</p>

          <h5 class="mt-4">Where the model is downloaded on disk</h5>
          <p>Once the embedding model has been downloaded, it is stored under the Chrome user data directory in an
            <span class="code">AIEmbeddings</span> folder, inside a version-numbered sub-folder (for example
            <span class="code">2026.5.19.1005</span>), as <span class="code">model.tflite</span>. Checking that this
            file exists is the quickest way to confirm the download actually completed.</p>
          <p>The version folder changes as Chrome ships new model revisions, and you may have more than one of them.
            The paths below differ per <strong>release channel</strong> — Stable, Beta, Dev and Canary each keep their
            own copy of the model, so make sure you are looking at the directory of the channel you are actually
            running.</p>

          <h6 class="mt-3">macOS</h6>
          <div style="max-height: 400px; overflow: auto">
            <table class="table table-responsive">
              <thead>
              <tr>
                <th>Channel</th>
                <th>Path</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <td>Stable</td>
                <td><span class="code">~/Library/Application Support/Google/Chrome/AIEmbeddings/&lt;version&gt;/model.tflite</span></td>
              </tr>
              <tr>
                <td>Beta</td>
                <td><span class="code">~/Library/Application Support/Google/Chrome Beta/AIEmbeddings/&lt;version&gt;/model.tflite</span></td>
              </tr>
              <tr>
                <td>Dev</td>
                <td><span class="code">~/Library/Application Support/Google/Chrome Dev/AIEmbeddings/&lt;version&gt;/model.tflite</span></td>
              </tr>
              <tr>
                <td>Canary</td>
                <td><span class="code">~/Library/Application Support/Google/Chrome Canary/AIEmbeddings/&lt;version&gt;/model.tflite</span></td>
              </tr>
              </tbody>
            </table>
          </div>

          <h6 class="mt-3">Windows</h6>
          <div style="max-height: 400px; overflow: auto">
            <table class="table table-responsive">
              <thead>
              <tr>
                <th>Channel</th>
                <th>Path</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <td>Stable</td>
                <td><span class="code">%LOCALAPPDATA%\Google\Chrome\User Data\AIEmbeddings\&lt;version&gt;\model.tflite</span></td>
              </tr>
              <tr>
                <td>Beta</td>
                <td><span class="code">%LOCALAPPDATA%\Google\Chrome Beta\User Data\AIEmbeddings\&lt;version&gt;\model.tflite</span></td>
              </tr>
              <tr>
                <td>Dev</td>
                <td><span class="code">%LOCALAPPDATA%\Google\Chrome Dev\User Data\AIEmbeddings\&lt;version&gt;\model.tflite</span></td>
              </tr>
              <tr>
                <td>Canary</td>
                <td><span class="code">%LOCALAPPDATA%\Google\Chrome SxS\User Data\AIEmbeddings\&lt;version&gt;\model.tflite</span></td>
              </tr>
              </tbody>
            </table>
          </div>

          <h6 class="mt-3">Linux</h6>
          <div style="max-height: 400px; overflow: auto">
            <table class="table table-responsive">
              <thead>
              <tr>
                <th>Channel</th>
                <th>Path</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <td>Stable</td>
                <td><span class="code">~/.config/google-chrome/AIEmbeddings/&lt;version&gt;/model.tflite</span></td>
              </tr>
              <tr>
                <td>Beta</td>
                <td><span class="code">~/.config/google-chrome-beta/AIEmbeddings/&lt;version&gt;/model.tflite</span></td>
              </tr>
              <tr>
                <td>Dev</td>
                <td><span class="code">~/.config/google-chrome-unstable/AIEmbeddings/&lt;version&gt;/model.tflite</span></td>
              </tr>
              <tr>
                <td>Canary</td>
                <td>Chrome Canary is not available on Linux — use Dev instead.</td>
              </tr>
              </tbody>
            </table>
          </div>

          <p class="mt-3">If you launch Chrome with a custom <span class="code">--user-data-dir</span>, the
            <span class="code">AIEmbeddings</span> folder lives under that directory instead of the defaults above.</p>
        </div>
      </app-page-accordion>
    </div>
  </div>

  <h2 class="display-6 mt-5"><i class="bi bi-play"></i> Playground</h2>
  <hr>

  <app-output
    [status]="this.embedStatus"
    [error]="this.error"
    [downloadProgress]="this.loaded"
    [outputCollapsed]="outputCollapsed"
    [statisticsCollapsed]="statisticsCollapsed"
    [output]="this.output"
    (abortExecution)="abortTriggered()"
    (abortExecutionFromCreate)="abortFromCreateTriggered()"
  >
    @if (rows.length > 0) {
      @if (metadata) {
        <div class="mb-3">
          @if (metadata.embeddingSpace) {
            <span class="code me-2">embeddingSpace: {{ metadata.embeddingSpace }}</span>
          }
          @if (metadata.maxInputTokens) {
            <span class="code">maxInputTokens: {{ metadata.maxInputTokens }}</span>
          }
        </div>
      }

      <h5>Embeddings</h5>
      <div style="max-height: 400px; overflow: auto">
        <table class="table table-responsive">
          <thead>
          <tr>
            <th>#</th>
            <th>Input</th>
            <th>Dimensions</th>
            <th>Tokens</th>
            <th>Values (first {{ previewLength }})</th>
          </tr>
          </thead>
          <tbody>
            @for (row of rows; track row.index) {
              <tr>
                <td>{{ row.index }}</td>
                <td>{{ row.input }}</td>
                <td>{{ row.dimensions }}</td>
                <td>
                  {{ row.tokenCount ?? '—' }}
                  @if (row.truncated) {
                    <span class="badge text-bg-warning ms-1">truncated</span>
                  }
                </td>
                <td><span class="code">{{ getPreview(row) }}</span></td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <h5 class="mt-4">Cosine similarity</h5>
      <p class="text-muted"><small>Computed in JavaScript from the returned vectors: the API does not expose a built-in
        comparison method.</small></p>
      <div style="max-height: 400px; overflow: auto">
        <table class="table table-responsive">
          <thead>
          <tr>
            <th></th>
            @for (row of rows; track row.index) {
              <th>#{{ row.index }}</th>
            }
          </tr>
          </thead>
          <tbody>
            @for (row of rows; track row.index) {
              <tr>
                <th>#{{ row.index }}</th>
                @for (similarity of similarityMatrix[row.index]; track $index) {
                  <td>
                    <span [class]="'similarity ' + getSimilarityClass(similarity)">{{ similarity | number: '1.4-4' }}</span>
                  </td>
                }
              </tr>
            }
          </tbody>
        </table>
      </div>

      <div class="d-flex flex-wrap gap-2 align-items-center mt-2">
        <small class="text-muted">Legend:</small>
        <span class="similarity similarity-very-high">≥ 0.85</span>
        <small class="text-muted me-2">near-duplicate</small>
        <span class="similarity similarity-high">≥ 0.65</span>
        <small class="text-muted me-2">related</small>
        <span class="similarity similarity-moderate">≥ 0.45</span>
        <small class="text-muted me-2">loosely related</small>
        <span class="similarity similarity-low">&lt; 0.45</span>
        <small class="text-muted">unrelated</small>
      </div>
      <p class="text-muted mt-2"><small>These thresholds are a reading aid, not a property of the API: the meaningful
        cut-off depends on the embedding space and on your task, so calibrate them on your own data.</small></p>
    }
  </app-output>

  <div class="row mt-4">
    <div class="col-12">
      <label for="input" class="form-label">Inputs <small class="text-muted">(one passage per line — they are sent
        as a single batch)</small></label>
      <textarea id="input" class="form-control" rows="8"
                placeholder="Enter one passage per line."
                [formControl]="inputFormControl"></textarea>
    </div>
  </div>

  <div class="row mt-3">
    <div class="col-12 col-md-6">
      <label for="task-type" class="form-label">Task type <small class="text-muted">(optional hint)</small></label>
      <select id="task-type" class="form-select" [formControl]="taskTypeFormControl">
        <option [ngValue]="null">None</option>
        @for (taskType of SemanticEmbedderTaskTypeEnum | keyvalue; track taskType.key) {
          <option [ngValue]="taskType.value">{{ taskType.value }}</option>
        }
      </select>
      <div class="form-text">Browsers may safely ignore this hint if their model does not support task types.</div>
    </div>
  </div>

  <div class="d-grid">
    <button class="btn btn-primary btn-lg mt-4" (click)="embed()">Embed</button>
  </div>

  <h2 class="display-6 mt-5"><i class="bi bi-code-square"></i> Runnable code examples</h2>
  <hr>

  <div class="row mt-4">
    <div class="col-12 col-xl-6">
      <app-card [status]="availabilityTaskStatus">
        <div header><h3 class="m-0">Availability</h3></div>
        <div class="card-body">
          <h5>Code</h5>
          <code-editor [code]="availabilityCode" [readonly]="true" [height]="'100px'"></code-editor>

          <h5 class="mt-3">Result</h5>
          <span class="code"
                [class.code-warning]="availabilityStatus === AvailabilityStatusEnum.Downloadable || availabilityStatus === AvailabilityStatusEnum.Downloading"
                [class.code-danger]="availabilityStatus === AvailabilityStatusEnum.Unavailable"
                [class.code-success]="availabilityStatus === AvailabilityStatusEnum.Available"
                [class.code-dark]="availabilityStatus === AvailabilityStatusEnum.Unknown"
          >{{ availabilityStatus }}</span>

          @if (availabilityError) {
            <div class="alert alert-danger m-0 mt-2 mb-2">{{ availabilityError }}</div>
          }

          <div class="row mt-5">
            <div class="col-12 d-grid">
              <button class="btn btn-outline-primary d-block" (click)="checkAvailability()">Check availability</button>
            </div>
          </div>
        </div>
      </app-card>
    </div>

    <div class="col-12 col-xl-6">
      <app-card [status]="embedStatus">
        <div header><h3 class="m-0">Embed</h3></div>
        <div class="card-body">
          <h5>Code</h5>
          <code-editor [code]="embedCode" [readonly]="true" [height]="'400px'"></code-editor>

          <div class="row mt-5">
            <div class="col-12 d-grid">
              <button class="btn btn-primary d-block" (click)="embed()">Embed</button>
            </div>
          </div>
        </div>
      </app-card>
    </div>
  </div>
</div>
